import { Autocomplete, Button, Grid, TextField } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import { BottomDrawer } from '../BottomDrawer/BottomDrawer.component';
import { Company } from '../../models';
import { useCompanies } from '../../hooks';
import { CompanyType } from '../../enums';
import { AddCompanyDrawerProps } from './AddCompanyDrawer.props';
import * as Sx from './AddCompanyDrawer.styles';

export const AddCompanyDrawer = ({
  currentCompany,
  onDoneClicked,
  onCancelClicked,
}: AddCompanyDrawerProps) => {
  const { t } = useTranslation();
  const [company, setCompany] = useState<Company | null>(null);
  const [selectableCompanies, setSelectableCompanies] = useState<Company[]>([]);

  const { list: companyList, loading } = useCompanies({
    includeDeleted: false,
    filters: [['type', '==', CompanyType.Customer]],
  });

  useEffect(() => {
    if (loading || !companyList || !currentCompany) return;

    const associatedCompaniesIds = currentCompany.associatedCompanies
      ? Object.keys(currentCompany.associatedCompanies).filter(
          (id) =>
            currentCompany.associatedCompanies &&
            !currentCompany.associatedCompanies[id].isDeleted
        )
      : [];

    const filteredCompanies = companyList.filter(
      (company) =>
        company.id !== currentCompany.id &&
        !associatedCompaniesIds.includes(company.id)
    );
    setSelectableCompanies(filteredCompanies);
  }, [loading, companyList, currentCompany]);

  const formButtons = () => (
    <Grid marginBottom={1.5}>
      <Button
        disabled={!company}
        onClick={() => onDoneClicked(company)}
        sx={Sx.confirmButton}
      >
        {t('forms.confirm')}
      </Button>

      <Button onClick={() => onCancelClicked()} sx={Sx.cancelButton}>
        {t('companies.actions.cancel')}
      </Button>
    </Grid>
  );

  return (
    <BottomDrawer
      title={t('companies.actions.addCompany')}
      icon={<HowToRegIcon sx={{ color: 'secondary.main' }} />}
      open={true}
      rightContent={formButtons()}
    >
      <Grid display="flex" width="25%" minWidth="500px" mt={2}>
        <Autocomplete
          fullWidth={true}
          popupIcon={<KeyboardArrowDownIcon />}
          options={selectableCompanies ?? []}
          value={company}
          onChange={(e, value) => {
            setCompany(value);
          }}
          getOptionLabel={(company) => company.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('companies.fields.company')}
              variant="filled"
              sx={Sx.input}
            />
          )}
          sx={Sx.companyInput}
        />
      </Grid>
    </BottomDrawer>
  );
};
